/** Signatures tool — callable wrappers. */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { SignatureRequest, SignatureField, SignatureTemplate, SignatureStamp, SignatureStampSaveInput } from '@daaily/shared';

export interface SignatureGetResponse {
  ok: true;
  request: SignatureRequest;
  sourceUrl: string;
  /** ORDERED requests only: already-signed signers' signature images (short-
   *  lived URLs) so the next signer's preview shows what came before. */
  priorSignatures?: { email: string; url: string }[];
  /** Contracts (`request.smsRequired`): the caller's own phone on file for the text-message code. */
  myPhone?: { masked: string | null; e164: string | null; problem: string | null } | null;
}

export const callSignatureCreate = httpsCallable<
  {
    title: string;
    message?: string | null;
    dueDate?: string | null;
    filename: string;
    fileBase64: string;
    signerEmails: string[];
    ordered?: boolean;
    auditTrailSeparate?: boolean;
    fields?: SignatureField[];
    vaultCategory?: string;
    copyRecipientEmails?: string[];
    signerOptions?: { email: string; vaultCategory?: string | null; role?: 'employee' | 'company' | null }[];
    requireSms?: boolean;
  },
  { ok: true; id: string }
>(functions, 'signatureCreateRequest');

export const callSignatureGet = httpsCallable<{ requestId: string }, SignatureGetResponse>(
  functions,
  'signatureGet',
);
export const callSignatureSign = httpsCallable<
  {
    requestId: string;
    imageBase64: string;
    consent: true;
    textValues?: Record<string, string>;
    initialsImageBase64?: string;
    saveSignatureToProfile?: boolean;
    phoneIdToken?: string;
  },
  { ok: true; completed: boolean }
>(functions, 'signatureSign');
export const callSignatureDecline = httpsCallable<{ requestId: string; reason: string }, { ok: true }>(
  functions,
  'signatureDecline',
);
export const callSignatureCancel = httpsCallable<{ requestId: string; reason: string }, { ok: true }>(
  functions,
  'signatureCancel',
);
export const callSignatureRemind = httpsCallable<{ requestId: string }, { ok: true; reminded: number }>(
  functions,
  'signatureRemind',
);
export const callSignatureSaveProfile = httpsCallable<
  { imageBase64: string | null; kind?: 'signature' | 'initials' },
  { ok: true; cleared: boolean }
>(functions, 'signatureSaveProfile');
export const callSignatureDownloadUrl = httpsCallable<
  { requestId: string; kind: 'source' | 'signed'; download?: boolean },
  { ok: true; url: string; filename: string }
>(functions, 'signatureDownloadUrl');

/** Download the separate audit trail PDF — sender-only. */
export const callSignatureAuditTrailUrl = httpsCallable<
  { requestId: string; download?: boolean },
  { ok: true; url: string; filename: string }
>(functions, 'signatureAuditTrailUrl');

/** Strip the data:...;base64, prefix. */
export const stripDataUrl = (dataUrl: string): string => dataUrl.replace(/^data:[^;]+;base64,/, '');

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(stripDataUrl(String(r.result)));
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(file);
  });

// ── Template callables ──────────────────────────────────────────────────────

export const callSignatureTemplateSave = httpsCallable<
  {
    id?: string;
    name: string;
    fileBase64?: string;
    filename?: string;
    placeholders: unknown[];
    signerRoles: unknown[];
    vaultCategory?: string;
    auditTrailSeparate?: boolean;
    ordered?: boolean;
    /** Sender enablement — empty array = available to ALL senders. */
    allowedSenderEmails?: string[];
  },
  { ok: true; id: string }
>(functions, 'signatureTemplateSave');

export const callSignatureTemplateList = httpsCallable<
  Record<string, never>,
  { ok: true; templates: SignatureTemplate[] }
>(functions, 'signatureTemplateList');

export const callSignatureTemplateDelete = httpsCallable<
  { templateId: string },
  { ok: true }
>(functions, 'signatureTemplateDelete');

export const callSignatureTemplateUrl = httpsCallable<
  { templateId: string; download?: boolean; asBytes?: boolean },
  { ok: true; url?: string; b64?: string; filename: string }
>(functions, 'signatureTemplateUrl');

export const callSignatureCreateFromTemplate = httpsCallable<
  {
    templateId: string;
    title: string;
    message?: string | null;
    dueDate?: string | null;
    values: Record<string, string>;
    roleSigners: Record<string, { email: string; name: string }>;
    ordered?: boolean;
    vaultCategory?: string;
    auditTrailSeparate?: boolean;
    requireSms?: boolean;
  },
  { ok: true; id: string }
>(functions, 'signatureCreateFromTemplate');

// Company stamps (Signatures → Stamps, Oct 2026).
export const callSignatureStampList = httpsCallable<
  Record<string, never>,
  { stamps: SignatureStamp[]; usableIds: string[]; canManage: boolean; eligible: { email: string; displayName: string }[] }
>(functions, 'signatureStampList');

export const callSignatureStampSave = httpsCallable<SignatureStampSaveInput, { ok: true; id: string }>(functions, 'signatureStampSave');

export const callSignatureStampDelete = httpsCallable<{ stampId: string }, { ok: true }>(functions, 'signatureStampDelete');
